import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useAuth } from '../../../auth/AuthProvider';
import { supabase } from '../../../lib/supabaseClient';
import { makeSafeObjectPath, uploadToBucket } from '../../../utils/storage';

const PAGE_SIZE = 20;

export const NoticeEditorModal = ({ isOpen, onClose, initial, onSaved }) => {
  const { user } = useAuth();
  const encodeBase64Utf8 = (obj) => {
    const json = JSON.stringify(obj);
    const bytes = new TextEncoder().encode(json);
    let binary = '';
    bytes.forEach((b) => {
      binary += String.fromCharCode(b);
    });
    return btoa(binary);
  };

  const getYoutubeVideoId = (rawUrl) => {
    const url = String(rawUrl || '').trim();
    if (!url) return '';
    const directId = url.match(/^[a-zA-Z0-9_-]{11}$/)?.[0];
    if (directId) return directId;
    try {
      const u = new URL(url);
      const host = (u.hostname || '').replace(/^www\./, '');
      if (host === 'youtu.be') return (u.pathname || '').replace('/', '').slice(0, 11);
      const v = u.searchParams.get('v');
      if (v) return v.slice(0, 11);
      const m = (u.pathname || '').match(/\/shorts\/([^/?#]+)/) || (u.pathname || '').match(/\/embed\/([^/?#]+)/);
      if (m?.[1]) return String(m[1]).slice(0, 11);
      return '';
    } catch {
      return '';
    }
  };

  const getYoutubeThumb = (rawUrl) => {
    const id = getYoutubeVideoId(rawUrl);
    if (!id) return '';
    return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
  };

  const decodeBase64Utf8 = (b64) => {
    const binary = atob(String(b64 || ''));
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);
    return JSON.parse(json);
  };

  const [title, setTitle] = useState('');
  const [authorName, setAuthorName] = useState('꿈현공장장');
  const [isPublished, setIsPublished] = useState(true);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [contentHtml, setContentHtml] = useState('');
  const [contentText, setContentText] = useState('');
  const [files, setFiles] = useState([]);

  const [inputMode, setInputMode] = useState('text');

  const [blocks, setBlocks] = useState([]);

  const [mode, setMode] = useState('edit');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setTitle(initial?.title || '');
    setAuthorName(initial?.author_name || '꿈현공장장');
    setIsPublished(initial?.is_published ?? true);
    setYoutubeUrl(initial?.youtube_url || '');
    const initHtml = initial?.content_html || '';
    const mainOnly = String(initHtml || '').replace(/<!--CH_BLOCKS:[\s\S]*?-->[\s\S]*$/m, '').trim();
    setContentHtml(mainOnly);
    try {
      const doc = new DOMParser().parseFromString(mainOnly || '', 'text/html');
      setContentText(String(doc?.body?.textContent || '').trim());
    } catch {
      setContentText('');
    }

    try {
      const m = String(initHtml || '').match(/<!--CH_BLOCKS:([\s\S]*?)-->/);
      if (m?.[1]) {
        const parsed = decodeBase64Utf8(m[1]);
        const nextBlocks = Array.isArray(parsed?.blocks) ? parsed.blocks : [];
        setBlocks(
          nextBlocks.map((b) => ({
            id: b?.id || String(Math.random()),
            text: b?.text || '',
            imagePath: b?.imagePath || '',
            imageOriginalName: b?.imageOriginalName || '',
            imageContentType: b?.imageContentType || '',
            imageFile: null,
          }))
        );
      } else {
        setBlocks([]);
      }
    } catch {
      setBlocks([]);
    }
    setFiles([]);
    setMode('edit');
    setInputMode('text');
    setError('');
    setIsSaving(false);
  }, [isOpen, initial?.id]);

  const escapeHtml = (s) =>
    String(s || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');

  const buildHtmlFromText = (text) => {
    const escaped = escapeHtml(text);
    const withBreaks = escaped.replaceAll('\n', '<br/>');
    return `<p>${withBreaks}</p>`;
  };

  const buildHtmlFromBlocks = (bList) => {
    const htmlParts = (bList || []).map((b) => {
      const safeText = buildHtmlFromText(b?.text || '').replace(/^<p>/, '').replace(/<\/p>$/, '');
      const imgUrl = b?.imagePath
        ? supabase.storage.from('notice-files').getPublicUrl(b.imagePath).data.publicUrl
        : '';
      const imgHtml = imgUrl
        ? `<div style="margin: 0 0 8px 0;"><img src="${imgUrl}" alt="${escapeHtml(b?.imageOriginalName || 'notice image')}" style="max-width: 100%; border-radius: 8px;"/></div>`
        : '';
      const textHtml = safeText ? `<div style="margin: 0;">${safeText}</div>` : '';
      return `<div style="margin: 0 0 18px 0;">${imgHtml}${textHtml}</div>`;
    });
    return htmlParts.join('');
  };

  const save = async () => {
    if (!user?.id) return;
    if (!String(title || '').trim()) {
      setError('공지명을 입력해주세요.');
      return;
    }
    const mainHtmlToSave = inputMode === 'html' ? contentHtml : buildHtmlFromText(contentText);
    const hasMain = !!String(mainHtmlToSave || '').trim();
    const hasBlocks = (blocks || []).some((b) => !!String(b?.text || '').trim() || !!b?.imageFile || !!b?.imagePath);
    if (!hasMain && !hasBlocks) {
      setError('내용을 입력해주세요.');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      let noticeId = initial?.id || '';

      const nextBlocks = [];
      for (const b of blocks || []) {
        const hasAny = !!String(b?.text || '').trim() || !!b?.imageFile || !!b?.imagePath;
        if (!hasAny) continue;

        let imagePath = b?.imagePath || '';
        let imageOriginalName = b?.imageOriginalName || '';
        let imageContentType = b?.imageContentType || '';

        if (b?.imageFile) {
          const f = b.imageFile;
          const path = makeSafeObjectPath({ userId: user.id, originalName: f.name, prefix: 'notice-blocks' });
          const { error: upErr } = await uploadToBucket({
            bucket: 'notice-files',
            path,
            blob: f,
            contentType: f.type || 'application/octet-stream',
            cacheControl: '31536000',
          });
          if (upErr) {
            setError(upErr.message || '블록 이미지 업로드에 실패했습니다.');
            setIsSaving(false);
            return;
          }
          imagePath = path;
          imageOriginalName = f.name;
          imageContentType = f.type || '';
        }

        nextBlocks.push({
          id: b?.id || String(Math.random()),
          text: b?.text || '',
          imagePath,
          imageOriginalName,
          imageContentType,
        });
      }

      const blocksMeta = nextBlocks.length > 0 ? encodeBase64Utf8({ blocks: nextBlocks }) : '';
      const blocksHtml = nextBlocks.length > 0 ? buildHtmlFromBlocks(nextBlocks) : '';
      const htmlToSave = `${String(mainHtmlToSave || '').trim()}${blocksMeta ? `\n\n<!--CH_BLOCKS:${blocksMeta}-->\n${blocksHtml}` : ''}`.trim();

      if (noticeId) {
        let updErr;
        ({ error: updErr } = await supabase
          .from('ch_notices')
          .update({
            title: String(title).trim(),
            author_name: String(authorName || '꿈현공장장').trim() || '꿈현공장장',
            is_published: !!isPublished,
            youtube_url: String(youtubeUrl || '').trim() || null,
            content_html: htmlToSave,
          })
          .eq('id', noticeId));

        const updMsg = String(updErr?.message || '');
        const updYoutubeMissing =
          updMsg.includes("Could not find the 'youtube_url'") ||
          (updMsg.includes('column') && updMsg.includes('youtube_url') && updMsg.includes('does not exist'));

        if (updErr && updYoutubeMissing) {
          ({ error: updErr } = await supabase
            .from('ch_notices')
            .update({
              title: String(title).trim(),
              author_name: String(authorName || '꿈현공장장').trim() || '꿈현공장장',
              is_published: !!isPublished,
              content_html: htmlToSave,
            })
            .eq('id', noticeId));
        }

        if (updErr) {
          setError(updErr.message || '공지 수정에 실패했습니다.');
          setIsSaving(false);
          return;
        }
      } else {
        let data;
        let insErr;

        ({ data, error: insErr } = await supabase
          .from('ch_notices')
          .insert({
            title: String(title).trim(),
            author_name: String(authorName || '꿈현공장장').trim() || '꿈현공장장',
            is_published: !!isPublished,
            youtube_url: String(youtubeUrl || '').trim() || null,
            content_html: htmlToSave,
          })
          .select('id')
          .maybeSingle());

        const insMsg = String(insErr?.message || '');
        const insYoutubeMissing =
          insMsg.includes("Could not find the 'youtube_url'") ||
          (insMsg.includes('column') && insMsg.includes('youtube_url') && insMsg.includes('does not exist'));

        if (insErr && insYoutubeMissing) {
          ({ data, error: insErr } = await supabase
            .from('ch_notices')
            .insert({
              title: String(title).trim(),
              author_name: String(authorName || '꿈현공장장').trim() || '꿈현공장장',
              is_published: !!isPublished,
              content_html: htmlToSave,
            })
            .select('id')
            .maybeSingle());
        }

        if (insErr) {
          setError(insErr.message || '공지 등록에 실패했습니다.');
          setIsSaving(false);
          return;
        }

        noticeId = data?.id || '';
      }

      if (noticeId && nextBlocks.length > 0) {
        const existing = new Set((initial?.files || []).map((f) => f?.file_path).filter(Boolean));
        const toInsert = nextBlocks
          .filter((b) => b?.imagePath && !existing.has(b.imagePath))
          .map((b) => ({
            notice_id: noticeId,
            file_path: b.imagePath,
            original_name: b.imageOriginalName || 'notice image',
            content_type: b.imageContentType || null,
            size_bytes: null,
          }));

        if (toInsert.length > 0) {
          const { error: metaErr } = await supabase.from('ch_notice_files').insert(toInsert);
          if (metaErr) {
            setError(metaErr.message || '파일 메타 저장에 실패했습니다.');
            setIsSaving(false);
            return;
          }
        }
      }

      if (noticeId && files.length > 0) {
        for (const f of files) {
          const path = makeSafeObjectPath({ userId: user.id, originalName: f.name, prefix: 'notice-files' });
          const { error: upErr } = await uploadToBucket({
            bucket: 'notice-files',
            path,
            blob: f,
            contentType: f.type || 'application/octet-stream',
            cacheControl: '31536000',
          });

          if (upErr) {
            setError(upErr.message || '파일 업로드에 실패했습니다.');
            setIsSaving(false);
            return;
          }

          const { error: metaErr } = await supabase.from('ch_notice_files').insert({
            notice_id: noticeId,
            file_path: path,
            original_name: f.name,
            content_type: f.type || null,
            size_bytes: f.size || null,
          });

          if (metaErr) {
            setError(metaErr.message || '파일 메타 저장에 실패했습니다.');
            setIsSaving(false);
            return;
          }
        }
      }

      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e?.message || '저장 중 오류가 발생했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-dialog bg-background/95 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-gaming-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-heading font-semibold text-foreground">
            {initial?.id ? '공지 수정' : '공지 등록'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-muted-foreground hover:bg-muted transition-gaming"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-sm text-destructive">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="공지명"
              type="text"
              value={title}
              onChange={(e) => setTitle(e?.target?.value)}
              placeholder="공지 제목"
            />
            <Input
              label="작성자"
              type="text"
              value={authorName}
              onChange={(e) => setAuthorName(e?.target?.value)}
              placeholder="꿈현공장장"
            />
          </div>

          <div>
            <Input
              label="유튜브 URL(선택)"
              type="text"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e?.target?.value)}
              placeholder="https://www.youtube.com/watch?v=..."
            />
            {getYoutubeThumb(youtubeUrl) ? (
              <div className="mt-3 rounded-md overflow-hidden border border-border bg-muted">
                <img
                  src={getYoutubeThumb(youtubeUrl)}
                  alt="youtube thumbnail"
                  className="w-full h-auto object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            ) : null}
          </div>

          <Checkbox
            label="게시"
            description="체크하면 사용자에게 노출됩니다"
            checked={isPublished}
            onChange={(e) => setIsPublished(e?.target?.checked)}
          />

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setMode('edit')}
              className={`px-3 py-2 rounded-md border transition-gaming text-sm font-medium ${
                mode === 'edit'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-foreground border-border hover:bg-muted'
              }`}
            >
              편집
            </button>
            <button
              type="button"
              onClick={() => setMode('preview')}
              className={`px-3 py-2 rounded-md border transition-gaming text-sm font-medium ${
                mode === 'preview'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-foreground border-border hover:bg-muted'
              }`}
            >
              미리보기
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setInputMode('text')}
              className={`px-3 py-2 rounded-md border transition-gaming text-sm font-medium ${
                inputMode === 'text'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-foreground border-border hover:bg-muted'
              }`}
            >
              일반 글
            </button>
            <button
              type="button"
              onClick={() => setInputMode('html')}
              className={`px-3 py-2 rounded-md border transition-gaming text-sm font-medium ${
                inputMode === 'html'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-foreground border-border hover:bg-muted'
              }`}
            >
              HTML
            </button>
          </div>

          {mode === 'edit' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {inputMode === 'html' ? '내용(HTML)' : '내용'}
                </label>
                <textarea
                  className="w-full px-4 py-3 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-gaming resize-none"
                  rows={10}
                  value={inputMode === 'html' ? contentHtml : contentText}
                  onChange={(e) => (inputMode === 'html' ? setContentHtml(e?.target?.value) : setContentText(e?.target?.value))}
                  placeholder={inputMode === 'html' ? '<p>내용</p>' : '내용을 입력하세요. (Enter로 줄바꿈 가능)'}
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-foreground">이미지+글(블록)</label>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() =>
                      setBlocks((prev) => [
                        ...(prev || []),
                        { id: String(Date.now()), text: '', imagePath: '', imageOriginalName: '', imageContentType: '', imageFile: null },
                      ])
                    }
                  >
                    블록 추가
                  </Button>
                </div>

                {(blocks || []).length === 0 ? (
                  <div className="p-4 bg-muted/30 rounded-md border border-border text-sm text-muted-foreground caption">
                    블록이 없습니다. “블록 추가”로 이미지+내용 블록을 추가하세요.
                  </div>
                ) : (
                  (blocks || []).map((b, idx) => {
                    const imgUrl = b?.imageFile
                      ? URL.createObjectURL(b.imageFile)
                      : (b?.imagePath ? supabase.storage.from('notice-files').getPublicUrl(b.imagePath).data.publicUrl : '');

                    return (
                      <div key={b.id} className="p-4 rounded-md border border-border bg-background space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium text-foreground">블록 {idx + 1}</div>
                          <button
                            type="button"
                            className="text-xs text-destructive hover:underline"
                            onClick={() => setBlocks((prev) => (prev || []).filter((x) => x.id !== b.id))}
                          >
                            삭제
                          </button>
                        </div>

                        {imgUrl ? (
                          <div className="rounded-md overflow-hidden border border-border bg-muted">
                            <img src={imgUrl} alt={b?.imageOriginalName || 'notice image'} className="w-full h-auto object-contain" />
                          </div>
                        ) : null}

                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const f = (e?.target?.files || [])[0];
                            if (!f) return;
                            setBlocks((prev) =>
                              (prev || []).map((x) =>
                                x.id === b.id
                                  ? {
                                      ...x,
                                      imageFile: f,
                                      imageOriginalName: f.name,
                                      imageContentType: f.type || '',
                                    }
                                  : x
                              )
                            );
                          }}
                          className="block w-full text-sm text-muted-foreground"
                        />

                        <textarea
                          className="w-full px-4 py-3 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-gaming resize-none"
                          rows={4}
                          value={b?.text || ''}
                          onChange={(e) =>
                            setBlocks((prev) =>
                              (prev || []).map((x) => (x.id === b.id ? { ...x, text: e?.target?.value } : x))
                            )
                          }
                          placeholder="이미지 아래에 표시할 내용을 입력하세요. (Enter로 줄바꿈 가능)"
                        />
                      </div>
                    );
                  })
                )}
              </div>
            </>
          ) : (
            <div className="border border-border rounded-md p-4 bg-background">
              <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{
                  __html:
                    `${String(inputMode === 'html' ? contentHtml : buildHtmlFromText(contentText)).trim()}${
                      (blocks || []).some((b) => !!String(b?.text || '').trim() || !!b?.imageFile || !!b?.imagePath)
                        ? `\n\n${buildHtmlFromBlocks(blocks)}`
                        : ''
                    }` || '',
                }}
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">첨부파일</label>
            <input
              type="file"
              multiple
              onChange={(e) => setFiles(Array.from(e?.target?.files || []))}
              className="block w-full text-sm text-muted-foreground"
            />
            {files.length > 0 && (
              <div className="text-xs text-muted-foreground caption mt-2">
                {files.map((f) => f.name).join(', ')}
              </div>
            )}
          </div>

          <div className="flex items-center space-x-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} fullWidth>
              취소
            </Button>
            <Button type="button" variant="default" onClick={save} fullWidth disabled={isSaving}>
              {isSaving ? '저장 중...' : '저장'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const NoticeManagementPanel = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const totalPages = Math.max(1, Math.ceil((totalCount || 0) / PAGE_SIZE));
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const load = useCallback(async () => {
    if (!user?.id) return;

    setIsLoading(true);
    setLoadError('');

    const from = (page - 1) * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    const { data, error, count } = await supabase
      .from('ch_notices')
      .select('id, title, author_name, is_published, created_at', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(from, to);

    if (error) {
      setRows([]);
      setTotalCount(0);
      setLoadError(error.message || '공지 목록을 불러오지 못했습니다.');
      setIsLoading(false);
      return;
    }

    setRows(data || []);
    setTotalCount(count || 0);
    setIsLoading(false);
  }, [page, user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    navigate('/admin-notice-editor');
  };

  const openEdit = async (id) => {
    if (!id) return;
    navigate(`/admin-notice-editor?id=${id}`);
  };

  const del = async (id) => {
    const ok = window.confirm('정말 삭제하시겠습니까?');
    if (!ok) return;

    const decodeBase64Utf8 = (b64) => {
      const binary = atob(String(b64 || ''));
      const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
      const json = new TextDecoder().decode(bytes);
      return JSON.parse(json);
    };

    const { data: noticeRow } = await supabase
      .from('ch_notices')
      .select('id, content_html')
      .eq('id', id)
      .maybeSingle();

    const { data: files } = await supabase
      .from('ch_notice_files')
      .select('id, file_path')
      .eq('notice_id', id);

    await supabase.from('ch_notice_files').delete().eq('notice_id', id);
    await supabase.from('ch_notices').delete().eq('id', id);

    const paths = new Set((files || []).map((f) => f.file_path).filter(Boolean));

    try {
      const m = String(noticeRow?.content_html || '').match(/<!--CH_BLOCKS:([\s\S]*?)-->/);
      if (m?.[1]) {
        const parsed = decodeBase64Utf8(m[1]);
        const blocks = Array.isArray(parsed?.blocks) ? parsed.blocks : [];
        blocks.forEach((b) => {
          if (b?.imagePath) paths.add(b.imagePath);
        });
      }
    } catch {
      // ignore
    }

    const pathList = Array.from(paths);
    if (pathList.length > 0) {
      await supabase.storage.from('notice-files').remove(pathList);
    }

    await load();
  };

  const items = useMemo(
    () =>
      (rows || []).map((n) => ({
        id: n.id,
        title: n.title,
        author: n.author_name || '꿈현공장장',
        date: n.created_at ? String(n.created_at).slice(0, 10) : '',
        published: !!n.is_published,
      })),
    [rows]
  );

  return (
    <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div>
          <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">공지사항</h2>
          <p className="text-xs md:text-sm text-muted-foreground caption mt-1">
            총 {totalCount}개 · {page}/{totalPages} 페이지
          </p>
        </div>
        <Button type="button" variant="default" onClick={openCreate}>
          새 공지
        </Button>
      </div>

      {loadError && (
        <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-sm text-destructive mb-4">
          {loadError}
        </div>
      )}

      {isLoading ? (
        <div className="p-4 bg-muted/30 rounded-md border border-border">
          <p className="text-sm text-muted-foreground caption">로딩 중...</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.length === 0 ? (
            <div className="p-4 bg-muted/30 rounded-md border border-border">
              <p className="text-sm text-muted-foreground caption">공지사항이 없습니다.</p>
            </div>
          ) : (
            items.map((n) => (
              <div key={n.id} className="p-3 md:p-4 bg-muted/50 rounded-md border border-border">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm md:text-base font-medium text-foreground truncate">{n.title}</div>
                    <div className="text-xs text-muted-foreground caption mt-1">
                      {n.date} · {n.author} · {n.published ? '게시' : '비게시'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openEdit(n.id)}
                      className="px-3 py-2 rounded-md bg-background border border-border hover:bg-muted transition-gaming text-sm"
                    >
                      수정
                    </button>
                    <button
                      type="button"
                      onClick={() => del(n.id)}
                      className="px-3 py-2 rounded-md bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-gaming text-sm"
                    >
                      삭제
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <div className="flex items-center justify-between mt-4">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={!canPrev}
          className={`px-4 py-2 rounded-md border transition-gaming text-sm ${
            canPrev ? 'bg-background border-border hover:bg-muted' : 'bg-muted/30 border-border text-muted-foreground'
          }`}
        >
          이전
        </button>
        <button
          type="button"
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={!canNext}
          className={`px-4 py-2 rounded-md border transition-gaming text-sm ${
            canNext ? 'bg-background border-border hover:bg-muted' : 'bg-muted/30 border-border text-muted-foreground'
          }`}
        >
          다음
        </button>
      </div>

    </div>
  );
};

export default NoticeManagementPanel;
